import { useMemo } from 'react'
import { useNow } from '@/shared/useNow'
import { focusQueueBase, isUnreachable } from './queue'
import type { ContactInfoFilter } from './queue'
import { useLeads, useLeadsFirstPage, type LeadListParams } from '@/shared/api/leads'
import { storedFirstPageSort } from '@/features/leads/table/useTableState'
import { canWorkQueue, useMe } from '@/shared/api/session'
import { CLOSED_STAGES } from '@/shared/domain'
import { compareFocusQueue, type FocusSort } from '@/features/leads/focus/focusSort'
import type { LeadRow } from '@/shared/types'
import type { View } from '@/shared/routes'

interface FocusContactCounts {
  all: number
  reachable: number
  unreachable: number
}

interface LeadsDataArgs {
  mode: View
  focusContactInfo: ContactInfoFilter
  focusSort: FocusSort
  /** Include the Waiting band and skipped leads. */
  showAll?: boolean
  /** Admin only: render this seller's queue instead of your own. */
  viewAs?: string | null
  /** Admin only: render the test queue of QA test leads instead. */
  testQueue?: boolean
  /** The URL names a queue that is not checked yet; Focus waits for it. */
  queuePending?: boolean
}

interface LeadsData {
  leads: LeadRow[]
  list: LeadRow[]
  focusContactCounts: FocusContactCounts
  me: ReturnType<typeof useMe>['data']
  leadsLoading: boolean
  /** Tabell shows its first page; the full list is still loading. */
  leadsPartial: boolean
  leadsError: ReturnType<typeof useLeads>['error']
  leadsData: ReturnType<typeof useLeads>['data']
  /** The leads callback reminders watch, in every view. */
  reminderLeads: LeadRow[]
  /** Per-band counts for the queue headers. Null outside focus mode. */
  bandCounts: Record<string | number, number> | null
}

/**
 * The lead lists a view loads: `list`, the one it renders, and `reminders`, the
 * one the callback reminders read.
 *
 * Only Focus and Tabell render a list. Översikt, Karta, admin and manage read
 * their own endpoints, so they load no list; Tabell's is the app's largest
 * response, 6.7 MB for 8000 leads. Their reminders read the seller's own
 * queue instead, the same request Focus makes and usually already cached.
 */
export function leadListsFor(
  mode: View,
  { showAll = false, viewAs = null, testQueue = false, worksQueue }: {
    showAll?: boolean; viewAs?: string | null; testQueue?: boolean; worksQueue: boolean
  },
): { list: LeadListParams | null; reminders: LeadListParams | null } {
  if (mode === 'focus') {
    // Focus normally loads active leads; when showing everything, the shared
    // queue predicate still hides closed stages.
    const list: LeadListParams = {
      segment: 'focus', includeStages: showAll ? [...CLOSED_STAGES] : [], showAll, asUser: viewAs, testQueue,
    }
    return { list, reminders: list }
  }
  // Table loads the full pipeline.
  if (mode === 'table') {
    const list: LeadListParams = { segment: 'all', includeStages: [...CLOSED_STAGES] }
    return { list, reminders: list }
  }
  // Only a seller has callbacks to be reminded of.
  return { list: null, reminders: worksQueue ? OWN_QUEUE : null }
}

// The seller's own Focus queue as it first opens: the cache entry Focus most
// often holds already.
const OWN_QUEUE: LeadListParams = { segment: 'focus' }

export function useLeadsData(
  { mode, focusContactInfo, focusSort, showAll = false, viewAs = null, testQueue = false, queuePending = false }: LeadsDataArgs,
): LeadsData {
  const tickingNow = useNow()
  const { data: me } = useMe()
  const lists = leadListsFor(mode, { showAll, viewAs, testQueue, worksQueue: canWorkQueue(me) })

  // Two segments: 'focus' is the BDR's actionable queue (server filters out
  // closed stages, future next_contact_at, and personal skips); 'all' is the
  // full list backing the Table view. A view without a list observes the own
  // queue's key, disabled, rather than Tabell's: a disabled observer still
  // holds its query in the cache.
  // Focus holds its request until ?queue= has been checked (YCRM-59), so a
  // reload never flashes the viewer's own queue before the chosen one.
  const waitForQueue = mode === 'focus' && queuePending
  const listQuery = useLeads(lists.list ?? OWN_QUEUE, { enabled: !!lists.list && !waitForQueue })
  // Tabell's first visit, with nothing cached, also asks for the first rows in
  // Tabell's order and renders them until the full list arrives (YCRM-83). A
  // revisit renders the cached list and asks for no first page.
  const firstSort = useMemo(() => (mode === 'table' ? storedFirstPageSort() : null), [mode])
  const firstPage = useLeadsFirstPage(
    lists.list ?? OWN_QUEUE, firstSort, mode === 'table' && listQuery.data === undefined,
  )
  const leadsPartial = mode === 'table' && listQuery.data === undefined && firstPage.data !== undefined
  const leadsData = lists.list ? (leadsPartial ? firstPage.data : listQuery.data) : undefined
  // A held query is not loading by react-query's measure, but the screen is.
  const leadsLoading = listQuery.isLoading || waitForQueue
  const leadsError = lists.list ? listQuery.error : null
  // Focus and Tabell remind from the list they render. The other views fetch
  // the queue without the minute poll, as they never polled before; a promise
  // made elsewhere arrives with the next window focus.
  const reminderQuery = useLeads(
    lists.reminders ?? OWN_QUEUE,
    { enabled: !!lists.reminders && !lists.list, poll: false },
  )

  // Straight from the query cache. This used to be a useState mirror kept in
  // sync by an effect, with a ref of outstanding patches re-applied on every
  // sync so a refetch landing mid-mutation could not roll a just-applied
  // outcome back into the queue. Mutations write their optimistic value into
  // the cache now, so there is nothing to mirror and nothing to re-apply.
  const leads = useMemo(() => leadsData?.leads ?? [], [leadsData])
  const reminderData = lists.list ? leadsData : reminderQuery.data
  const reminderLeads = useMemo(() => reminderData?.leads ?? [], [reminderData])

  // Sort matches the server: not-skipped first (so fresh leads sit at the top
  // of the queue). Lets the BDR power
  // through the active queue and revisit skipped leads at the tail.
  //
  // Focus mode also mirrors the server-side filters:
  //   - hides promises beyond today unless showing everything
  //   - drops leads in any of the CLOSED_STAGES (Booked / Handed Over /
  //     Disqualified / Existing Customer / Wrong Contact)
  // Without the closed-stage filter the BDR sees a lead they just marked
  // "Already Customer" pop back into focus the moment they hit ← because
  // local state still holds the optimistic update with the new stage, and
  // the local list doesn't realise that stage is meant to be hidden.
  const focusBase = useMemo(
    () => mode === 'focus' ? focusQueueBase(leads, showAll, tickingNow) : leads,
    [leads, mode, showAll, tickingNow],
  )

  const list = useMemo(() => {
    // Advances every 30s while the tab is visible, so a lead promised for this
    // afternoon becomes callable at the promised time instead of whenever
    // something else happens to refetch.
    const now = tickingNow
    // Focus-mode filters minus the contact-info one. Kept separate so the
    // per-mode contact-info counts (All / Reachable / No contact) can be
    // computed against the same base — clicking a chip shows exactly the
    // number of leads the chip's badge promised.
    const filtered = mode === 'focus' && focusContactInfo !== 'all'
      ? focusBase.filter(l => {
          const u = isUnreachable(l)
          return focusContactInfo === 'reachable' ? !u : u
        })
      : focusBase
    return [...filtered].sort((a, b) => {
      if (mode === 'focus') return compareFocusQueue(a, b, now, focusSort)
      const skipDelta = (a.skipped ? 1 : 0) - (b.skipped ? 1 : 0)
      if (skipDelta) return skipDelta
      return a.id.localeCompare(b.id)
    })
  }, [focusBase, mode, focusContactInfo, focusSort, tickingNow])

  // Per-bucket counts for the contact-info filter chips. Computed against
  // the same base as `list` (i.e. focus filters applied, contact-info NOT)
  // so the chip badges read true: clicking "No contact" surfaces exactly
  // unreachable leads in the queue.
  const focusContactCounts = useMemo(() => {
    if (mode !== 'focus') return { all: 0, reachable: 0, unreachable: 0 }
    const unreachable = focusBase.filter(isUnreachable).length
    return { all: focusBase.length, reachable: focusBase.length - unreachable, unreachable }
  }, [focusBase, mode])

  // Counts per band, over everything the seller owns rather than over `list` —
  // the headers have to be able to say "264 waiting" precisely when those 264 are
  // NOT being rendered. That gap going unexplained is what made Focus look broken.
  // Straight from the server: it counts the seller's whole book, including the
  // leads this queue is deliberately not showing. A client-side count could only
  // ever tally what was fetched, which is exactly the number that is not in
  // question.
  const bandCounts = useMemo(
    () => (mode === 'focus' ? (leadsData?.bandCounts ?? null) : null),
    [mode, leadsData],
  )

  return {
    leads,
    list,
    bandCounts,
    focusContactCounts,
    me,
    leadsLoading,
    leadsPartial,
    leadsError,
    leadsData,
    reminderLeads,
  }
}
